import React, { useState, useEffect } from 'react';
import './usertools-css/VitaminDCalculator.css';
import '../../styles/components/ainutritionlandingpage.css';


const omega3AITable = { // AI for ALA (mg/day) - Based on IOM
  'male_19+': 1600,
  'female_19+': 1100,
  'pregnant': 1400,
  'lactating': 1300,
  'default': 1100, // Fallback
};

const epaDhaTargetTable = { // Target EPA+DHA (mg/day) - Consensus/AHA
  general: { key: 'general', min: 250, max: 500, recommended: 300 }, // Using 300 as a midpoint target
  pregnant_lactating: { key: 'pregnant_lactating', min: 300, max: 500, recommended: 300, dha_emphasis: 200 }, // Min 200-300 DHA specifically
  cvd_secondary: { key: 'cvd_secondary', min: 500, max: 1000, recommended: 1000 }, // AHA recommendation for known heart disease
  hyper_tg: { key: 'hyper_tg', min: 2000, max: 4000, recommended: 2000 }, // Therapeutic dose, requires MD supervision
};

const SAFETY_UL_SUPPLEMENTAL_EPA_DHA = 3000; // mg/day from supplements (FDA advisory)
const SAFETY_ANTICOAGULANT_THRESHOLD = 1000; // mg/day supplemental EPA+DHA (caution threshold)

const marineFoodData = {
  salmon_atlantic: { epa_dha_mg_per_100g: 1800, is_low_mercury: true },
  sardines_canned: { epa_dha_mg_per_100g: 1480, is_low_mercury: true },
  mackerel_atlantic: { epa_dha_mg_per_100g: 1300, is_low_mercury: true }, // Atlantic is lower than King
  trout_rainbow: { epa_dha_mg_per_100g: 900, is_low_mercury: true },
  herring: { epa_dha_mg_per_100g: 1700, is_low_mercury: true },
  anchovy: { epa_dha_mg_per_100g: 2100, is_low_mercury: true },
  tuna_light_canned: { epa_dha_mg_per_100g: 230, is_low_mercury: true }, // Light is lower mercury
  shrimp: { epa_dha_mg_per_100g: 120, is_low_mercury: true },
  cod: { epa_dha_mg_per_100g: 150, is_low_mercury: true }, // Common lower fat fish
  tuna_albacore_canned: { epa_dha_mg_per_100g: 730, is_low_mercury: false },
  swordfish: { epa_dha_mg_per_100g: 750, is_low_mercury: false },
  shark: { epa_dha_mg_per_100g: 800, is_low_mercury: false },
  king_mackerel: { epa_dha_mg_per_100g: 1100, is_low_mercury: false },
  tilefish: { epa_dha_mg_per_100g: 1000, is_low_mercury: false },
};
const LOW_MERCURY_FISH_LIST = Object.entries(marineFoodData)
  .filter(([, data]) => data.is_low_mercury)
  .map(([key]) => key.replace(/_/g, ' '));
const HIGH_MERCURY_FISH_LIST = Object.entries(marineFoodData)
  .filter(([, data]) => !data.is_low_mercury)
  .map(([key]) => key.replace(/_/g, ' '));

const plantAlaData = {
  flaxseed_oil_tbsp: { ala_mg_per_serving: 7300, serving_unit: 'Tbsp' },
  chia_seeds_tbsp: { ala_mg_per_serving: 2500, serving_unit: 'Tbsp' },
  flaxseeds_ground_tbsp: { ala_mg_per_serving: 1600, serving_unit: 'Tbsp' },
  walnuts_oz: { ala_mg_per_serving: 2500, serving_unit: 'oz (approx 1/4 cup)' },
  canola_oil_tbsp: { ala_mg_per_serving: 1300, serving_unit: 'Tbsp' },
  soybean_oil_tbsp: { ala_mg_per_serving: 900, serving_unit: 'Tbsp' },
};

const supplementCatalog = [
  { id: 'fish_oil_1000', name: 'Fish Oil 1000mg capsule', type: 'fish-oil', epa_mg: 180, dha_mg: 120, ala_mg: 0 },
  { id: 'fish_oil_conc', name: 'Concentrated Fish Oil (TG form, typical)', type: 'fish-oil-conc', epa_mg: 300, dha_mg: 200, ala_mg: 0 },
  { id: 'algal_dha_300', name: 'Algal Oil DHA 300mg capsule', type: 'algal-dha', epa_mg: 0, dha_mg: 300, ala_mg: 0 },
  { id: 'algal_epa_dha', name: 'Algal Oil EPA+DHA (typical)', type: 'algal-epa-dha', epa_mg: 150, dha_mg: 300, ala_mg: 0 },
  { id: 'krill_oil_500', name: 'Krill Oil 500mg capsule (typical)', type: 'krill-oil', epa_mg: 60, dha_mg: 30, ala_mg: 0 },
  { id: 'cod_liver_oil_tsp', name: 'Cod Liver Oil (per tsp/5ml)', type: 'cod-liver-oil', epa_mg: 400, dha_mg: 400, ala_mg: 50 }, // Highly variable
  { id: 'rx_epa_4g', name: 'Rx High EPA (e.g., Vascepa) - REFERENCE ONLY', type: 'rx-epa', epa_mg: 4000, dha_mg: 0, ala_mg: 0 }, // Per daily dose, MD supervised
  { id: 'flax_oil_softgel', name: 'Flaxseed Oil 1000mg softgel', type: 'flax-oil', epa_mg: 0, dha_mg: 0, ala_mg: 500 }, // ~50% ALA
  { id: 'other', name: 'Other/Custom Supplement', type: 'other', epa_mg: 0, dha_mg: 0, ala_mg: 0 },
];

const alaConversionRates = {
  base_ala_to_epa: 0.05, // 5%
  base_ala_to_dha: 0.02, // 2%
  female_preg_multiplier: 1.5, // Increase by 50%
  high_n6_multiplier: 0.75, // Decrease by 25%
};

// ---------- Helpers ----------
const getAlaIntakeKey = (sex, isPregnant, isLactating) => {
  if (isPregnant) return 'pregnant';
  if (isLactating) return 'lactating';
  if (sex === 'male') return 'male_19+';
  if (sex === 'female') return 'female_19+';
  return 'default'; // Fallback
};

const getEpaDhaTarget = (isPregnant, isLactating, healthFlags) => {
  if (healthFlags.hasHyperTG) return epaDhaTargetTable.hyper_tg;
  if (healthFlags.hasCVD) return epaDhaTargetTable.cvd_secondary;
  if (isPregnant || isLactating) return epaDhaTargetTable.pregnant_lactating;
  return epaDhaTargetTable.general;
};

const ozToGrams = (oz) => (oz || 0) * 28.3495;
const toNumber = (v) => Number(v) || 0;
const round = (n) => Math.round(n);
const formatMg = (n) => round(n).toLocaleString();

// ---------- Component ----------
function Omega3EssentialFattyAcidCalculator() {
  // --- State for User Inputs ---
  const [age, setAge] = useState(''); // Keep age for context if needed later
  const [sex, setSex] = useState(''); // 'male', 'female'
  const [isPregnant, setIsPregnant] = useState(false);
  const [isLactating, setIsLactating] = useState(false);
  const [isVeganVegetarian, setIsVeganVegetarian] = useState(false);

  const [fishDiary, setFishDiary] = useState([{ fishType: '', servingsPerWeek: '', portionOz: '' }]);

  const [plantSources, setPlantSources] = useState({
    flaxseed_oil_tbsp: '',
    chia_seeds_tbsp: '',
    flaxseeds_ground_tbsp: '',
    walnuts_oz: '',
    canola_oil_tbsp: '',
    soybean_oil_tbsp: '',
  });

  const [fortifiedFoods, setFortifiedFoods] = useState({
    omega3_eggs: '', // servings/week
    omega3_milk: '', // cups/week
    omega3_spread: '', // servings/week
  });
  const FORTIFIED_EGG_EPA_DHA = 50; // mg per egg (example)
  const FORTIFIED_MILK_EPA_DHA = 30; // mg per cup (example)
  const FORTIFIED_SPREAD_EPA_DHA = 40; // mg per serving (example)

  // Supplement Use
  const [takesSupplement, setTakesSupplement] = useState(false);
  const [selectedSupplementId, setSelectedSupplementId] = useState('');
  const [customSupplementEPA, setCustomSupplementEPA] = useState('');
  const [customSupplementDHA, setCustomSupplementDHA] = useState('');
  const [customSupplementALA, setCustomSupplementALA] = useState('');
  const [supplementDosesPerWeek, setSupplementDosesPerWeek] = useState('');

  // n-6 Intake Proxy & Health Flags
  const [highN6Intake, setHighN6Intake] = useState(false);
  const [healthFlags, setHealthFlags] = useState({
    hasCVD: false, // Cardiovascular disease history
    hasHyperTG: false, // High triglycerides
    hasBleedingDisorder: false, // Or easy bruising/bleeding
    takesAnticoagulant: false, // e.g., Warfarin, DOACs, high-dose aspirin
  });

  const [mercuryConcern, setMercuryConcern] = useState(false);
  useEffect(() => {
    // Automatically set mercury concern for pregnant/lactating women
    setMercuryConcern(Boolean(isPregnant || isLactating));
  }, [isPregnant, isLactating]);

  // --- State for Results ---
  const [calculationResult, setCalculationResult] = useState(null);
  const [showResults, setShowResults] = useState(false);

  // --- Input Handlers ---
  const handleFishDiaryChange = (index, event) => {
    const { name, value } = event.target;
    const updatedDiary = [...fishDiary];
    updatedDiary[index][name] = value;
    setFishDiary(updatedDiary);
  };
  const addFishEntry = () => setFishDiary([...fishDiary, { fishType: '', servingsPerWeek: '', portionOz: '' }]);
  const removeFishEntry = (index) => setFishDiary(fishDiary.filter((_, i) => i !== index));

  const handlePlantSourceChange = (event) => {
    const { name, value } = event.target;
    setPlantSources((prev) => ({ ...prev, [name]: value || '' }));
  };
  const handleFortifiedFoodChange = (event) => {
    const { name, value } = event.target;
    setFortifiedFoods((prev) => ({ ...prev, [name]: value || '' }));
  };
  const handleHealthFlagChange = (event) => {
    const { name, checked } = event.target;
    setHealthFlags((prev) => ({ ...prev, [name]: checked }));
  };
  const handleSupplementSelection = (event) => {
    const id = event.target.value;
    setSelectedSupplementId(id);
    if (id !== 'other') {
      setCustomSupplementEPA('');
      setCustomSupplementDHA('');
      setCustomSupplementALA('');
    }
  };

  // --- Calculation Logic ---
  const calculateOmega3Status = (e) => {
    e.preventDefault();
    setShowResults(false);
    setCalculationResult(null);

    const explanation = [];
    const safetyFlags = [];
    const suggestions = { food: [], supplement: [], lifestyle: [], safety: [], mercury: [] };
    const mercuryWarnings = []; // Specific warnings about chosen fish

    if (!sex) {
      alert('Please select Sex.');
      return;
    }

    // Targets
    const alaTargetKey = getAlaIntakeKey(sex, isPregnant, isLactating);
    const targetAla = omega3AITable[alaTargetKey] || omega3AITable.default;
    explanation.push(`Target ALA (Adequate Intake): ${targetAla} mg/day (based on ${alaTargetKey.replace(/_/g, ' ')}).`);

    const currentTargetEpaDhaRange = getEpaDhaTarget(isPregnant, isLactating, healthFlags);
    const targetEpaDha = currentTargetEpaDhaRange.recommended;
    explanation.push(`Target EPA+DHA: ${currentTargetEpaDhaRange.min}-${currentTargetEpaDhaRange.max} mg/day (Recommended: ${targetEpaDha} mg/day).`);
    if (currentTargetEpaDhaRange.dha_emphasis) {
      explanation.push(`  - Includes emphasis on at least ${currentTargetEpaDhaRange.dha_emphasis} mg/day DHA for pregnancy/lactation.`);
    }
    if (healthFlags.hasHyperTG) {
      explanation.push(`  - Note: High triglyceride target (${targetEpaDha}mg) typically requires prescription fish oil under medical supervision.`);
      safetyFlags.push({
        type: 'severe',
        text: `The high EPA+DHA target for hypertriglyceridemia (${targetEpaDha}mg) should only be pursued under the guidance of a healthcare provider, often using prescription-strength supplements.`,
      });
    }
    if (isVeganVegetarian) {
      explanation.push(`Note: As a vegan/vegetarian, EPA/DHA intake relies heavily on ALA conversion or algal oil supplements.`);
      suggestions.lifestyle.push(
        'For vegetarians/vegans: Pay close attention to ALA sources and consider an algal oil supplement for direct EPA/DHA.'
      );
    }

    // --- Food Intake ---
    let food_epa_dha_total_mg_day = 0;
    let food_ala_total_mg_day = 0;
    let food_high_mercury_servings_week = 0;
    let totalFishServingsPerWeek = 0;

    explanation.push('\n--- Food Intake ---');
    fishDiary.forEach((entry, index) => {
      const fishKey = entry.fishType;
      const servings = toNumber(entry.servingsPerWeek);
      const portion = toNumber(entry.portionOz);

      if (fishKey && servings > 0 && portion > 0 && marineFoodData[fishKey]) {
        const fishData = marineFoodData[fishKey];
        const portionGrams = ozToGrams(portion);
        const epaDhaPerServing = (portionGrams / 100) * fishData.epa_dha_mg_per_100g;
        const weeklyEpaDha = servings * epaDhaPerServing;
        food_epa_dha_total_mg_day += weeklyEpaDha / 7;
        totalFishServingsPerWeek += servings;

        explanation.push(
          `Fish ${index + 1} (${fishKey.replace(/_/g, ' ')}): ${servings.toFixed(1)} servings/wk * ${portion.toFixed(
            1
          )} oz (~${round(portionGrams)}g) -> ~${round(weeklyEpaDha / 7)} mg EPA+DHA/day.`
        );

        if (!fishData.is_low_mercury) {
          food_high_mercury_servings_week += servings;
          if (mercuryConcern) {
            mercuryWarnings.push(
              `Choice ${index + 1}: ${fishKey.replace(/_/g, ' ')} (${servings.toFixed(
                1
              )} servings/wk) is high in mercury. Avoid or strictly limit during pregnancy/lactation/childhood. Choose low-mercury options instead.`
            );
          }
        }
      }
    });
    explanation.push(`Subtotal from Fish: ${round(food_epa_dha_total_mg_day)} mg EPA+DHA/day.`);

    // Plant ALA Sources (weekly -> daily)
    Object.entries(plantAlaData).forEach(([sourceKey, data]) => {
      const servings = toNumber(plantSources[sourceKey]); // per week
      if (servings > 0) {
        const weeklyAla = servings * data.ala_mg_per_serving;
        food_ala_total_mg_day += weeklyAla / 7;
        explanation.push(
          `Plant Source (${sourceKey.replace(/_/g, ' ')}): ${servings.toFixed(1)} servings/wk -> ~${round(
            weeklyAla / 7
          )} mg ALA/day.`
        );
      }
    });
    explanation.push(`Subtotal from Plant Sources: ${round(food_ala_total_mg_day)} mg ALA/day.`);

    // Fortified Foods EPA/DHA (example estimates)
    let fortified_epa_dha_mg_day = 0;
    const eggServings = toNumber(fortifiedFoods.omega3_eggs);
    const milkServings = toNumber(fortifiedFoods.omega3_milk);
    const spreadServings = toNumber(fortifiedFoods.omega3_spread);
    if (eggServings > 0) fortified_epa_dha_mg_day += (eggServings * FORTIFIED_EGG_EPA_DHA) / 7;
    if (milkServings > 0) fortified_epa_dha_mg_day += (milkServings * FORTIFIED_MILK_EPA_DHA) / 7;
    if (spreadServings > 0) fortified_epa_dha_mg_day += (spreadServings * FORTIFIED_SPREAD_EPA_DHA) / 7;
    if (fortified_epa_dha_mg_day > 0) {
      explanation.push(`Subtotal from Fortified Foods: ${round(fortified_epa_dha_mg_day)} mg EPA+DHA/day (estimated).`);
      food_epa_dha_total_mg_day += fortified_epa_dha_mg_day;
    }

    explanation.push('-> Total Estimated DIETARY Intake:');
    explanation.push(`    EPA+DHA: ${round(food_epa_dha_total_mg_day)} mg/day`);
    explanation.push(`    ALA:     ${round(food_ala_total_mg_day)} mg/day`);

    // --- Supplements (compute before ALA conversion so we include supplemental ALA in conversion) ---
    explanation.push('\n--- Supplement Intake ---');
    let supp_epa_mg_day = 0;
    let supp_dha_mg_day = 0;
    let supp_ala_mg_day = 0;
    let supp_total_epa_dha_mg_day = 0;
    let perDoseEpaDhaMg = 0;

    if (takesSupplement) {
      let doseEpa = 0;
      let doseDha = 0;
      let doseAla = 0;
      const dosesWeek = toNumber(supplementDosesPerWeek);
      let suppName = 'Custom Supplement';

      if (selectedSupplementId && selectedSupplementId !== 'other') {
        const selectedSupp = supplementCatalog.find((s) => s.id === selectedSupplementId);
        if (selectedSupp) {
          doseEpa = toNumber(selectedSupp.epa_mg);
          doseDha = toNumber(selectedSupp.dha_mg);
          doseAla = toNumber(selectedSupp.ala_mg);
          suppName = selectedSupp.name;
          explanation.push(
            `Supplement selected: ${suppName} (EPA ${doseEpa}mg, DHA ${doseDha}mg, ALA ${doseAla}mg per dose).`
          );
        }
      } else if (selectedSupplementId === 'other') {
        doseEpa = toNumber(customSupplementEPA);
        doseDha = toNumber(customSupplementDHA);
        doseAla = toNumber(customSupplementALA);
        explanation.push(`Custom supplement dose: EPA ${doseEpa}mg, DHA ${doseDha}mg, ALA ${doseAla}mg per dose.`);
      }

      if (dosesWeek > 0 && (doseEpa + doseDha + doseAla) > 0) {
        perDoseEpaDhaMg = doseEpa + doseDha; // for per-dose safety note
        supp_epa_mg_day = (doseEpa * dosesWeek) / 7;
        supp_dha_mg_day = (doseDha * dosesWeek) / 7;
        supp_ala_mg_day = (doseAla * dosesWeek) / 7;
        supp_total_epa_dha_mg_day = supp_epa_mg_day + supp_dha_mg_day;

        explanation.push('Doses per week: ' + dosesWeek + '.');
        explanation.push('Average Daily Supplemental Intake:');
        explanation.push(`    EPA: ${round(supp_epa_mg_day)} mg/day`);
        explanation.push(`    DHA: ${round(supp_dha_mg_day)} mg/day`);
        explanation.push(`    ALA: ${round(supp_ala_mg_day)} mg/day`);
      } else {
        explanation.push('No valid supplement dose or frequency entered. Supplemental intake set to 0.');
      }
    } else {
      explanation.push('No supplement use reported.');
    }

    // --- ALA Conversion (includes supplemental ALA) ---
    explanation.push('\n--- ALA Conversion ---');
    let conversionRateEPA = alaConversionRates.base_ala_to_epa;
    let conversionRateDHA = alaConversionRates.base_ala_to_dha;
    const conversionFactorsApplied = [];

    if (sex === 'female' || isPregnant || isLactating) {
      conversionRateEPA *= alaConversionRates.female_preg_multiplier;
      conversionRateDHA *= alaConversionRates.female_preg_multiplier;
      conversionFactorsApplied.push(
        `Female sex or pregnancy (+${((alaConversionRates.female_preg_multiplier - 1) * 100).toFixed(0)}%)`
      );
    }
    if (highN6Intake) {
      conversionRateEPA *= alaConversionRates.high_n6_multiplier;
      conversionRateDHA *= alaConversionRates.high_n6_multiplier;
      conversionFactorsApplied.push(
        `High n-6 Intake (${((1 - alaConversionRates.high_n6_multiplier) * 100).toFixed(0)}% reduction)`
      );
      suggestions.lifestyle.push(
        'High intake of omega-6 fats (common in processed foods, corn/soybean oil) can reduce ALA conversion. Aim for a better n-6:n-3 balance (e.g., use olive/avocado oil, reduce processed snacks).'
      );
    }

    explanation.push(
      `Base rates: ALA->EPA ~${(alaConversionRates.base_ala_to_epa * 100).toFixed(1)}%, ALA->DHA ~${(
        alaConversionRates.base_ala_to_dha * 100
      ).toFixed(1)}%`
    );
    if (conversionFactorsApplied.length > 0) {
      explanation.push(`Adjustments: ${conversionFactorsApplied.join(', ')}`);
    }
    explanation.push(
      `Effective Conversion Rates: ALA->EPA ~${(conversionRateEPA * 100).toFixed(1)}%, ALA->DHA ~${(
        conversionRateDHA * 100
      ).toFixed(1)}%`
    );

    const totalAlaMgDay = food_ala_total_mg_day + supp_ala_mg_day;
    const epa_from_ala_mg_day = totalAlaMgDay * conversionRateEPA;
    const dha_from_ala_mg_day = totalAlaMgDay * conversionRateDHA;

    explanation.push(`Estimated Conversion from TOTAL ALA (${round(totalAlaMgDay)} mg/day):`);
    explanation.push(`    ~${round(epa_from_ala_mg_day)} mg/day to EPA`);
    explanation.push(`    ~${round(dha_from_ala_mg_day)} mg/day to DHA`);

    // --- Totals ---
    explanation.push('\n--- Total Estimated Intake ---');
    const total_epa_dha =
      food_epa_dha_total_mg_day + epa_from_ala_mg_day + dha_from_ala_mg_day + supp_epa_mg_day + supp_dha_mg_day;

    // Approximate split: assume food EPA/DHA ≈ 60/40
    const food_epa_approx = food_epa_dha_total_mg_day * 0.6;
    const food_dha_approx = food_epa_dha_total_mg_day * 0.4;
    const total_epa = epa_from_ala_mg_day + supp_epa_mg_day + food_epa_approx;
    const total_dha = dha_from_ala_mg_day + supp_dha_mg_day + food_dha_approx;

    explanation.push(`Total Estimated ALA: ${round(totalAlaMgDay)} mg/day`);
    explanation.push(`Total Estimated EPA+DHA: ${round(total_epa_dha)} mg/day`);
    explanation.push(`  (Includes ~${round(epa_from_ala_mg_day + dha_from_ala_mg_day)} mg from ALA conversion)`);
    explanation.push(`  (Approximate breakdown: EPA ~${round(total_epa)} mg, DHA ~${round(total_dha)} mg)`);

    // --- Assessment & Safety ---
    explanation.push('\n--- Assessment ---');
    const epa_dha_gap = targetEpaDha - total_epa_dha;
    const ala_gap = targetAla - totalAlaMgDay;

    explanation.push(`ALA vs Target (${targetAla} mg): ${ala_gap > 0 ? `${round(ala_gap)} mg deficit` : 'Met or Exceeded'}.`);
    explanation.push(
      `EPA+DHA vs Target (${targetEpaDha} mg): ${epa_dha_gap > 0 ? `${round(epa_dha_gap)} mg deficit` : 'Met or Exceeded'}.`
    );

    // Supplemental safety: average daily
    if (supp_total_epa_dha_mg_day > SAFETY_UL_SUPPLEMENTAL_EPA_DHA) {
      safetyFlags.push({
        type: 'warning',
        text: `Your SUPPLEMENTAL EPA+DHA intake (~${round(
          supp_total_epa_dha_mg_day
        )} mg/day) exceeds the FDA advisory limit of ${SAFETY_UL_SUPPLEMENTAL_EPA_DHA} mg/day. High doses may increase bleeding risk or affect immune function. Discuss with your doctor.`,
      });
      suggestions.safety.push(
        `Reduce supplemental EPA+DHA intake to below ${SAFETY_UL_SUPPLEMENTAL_EPA_DHA} mg/day unless specifically prescribed and monitored by your doctor.`
      );
    }

    const bleedRiskCondition = healthFlags.hasBleedingDisorder || healthFlags.takesAnticoagulant;
    if (bleedRiskCondition && supp_total_epa_dha_mg_day > SAFETY_ANTICOAGULANT_THRESHOLD) {
      safetyFlags.push({
        type: 'warning',
        text: `You reported a bleeding disorder or anticoagulant use, and your SUPPLEMENTAL EPA+DHA intake (~${round(
          supp_total_epa_dha_mg_day
        )} mg/day) exceeds the cautionary threshold of ${SAFETY_ANTICOAGULANT_THRESHOLD} mg/day. This combination may significantly increase bleeding risk. Consult your doctor immediately.`,
      });
      suggestions.safety.push(
        `If you take anticoagulants or have a bleeding disorder, doses of EPA+DHA supplements above ${SAFETY_ANTICOAGULANT_THRESHOLD} mg/day require close medical supervision. Discuss with your doctor before continuing or starting high-dose supplements.`
      );
    } else if (bleedRiskCondition && supp_total_epa_dha_mg_day > 0) {
      safetyFlags.push({
        type: 'info',
        text: `Bleeding disorder or anticoagulant use noted. Be mindful that even moderate supplemental EPA+DHA (~${round(
          supp_total_epa_dha_mg_day
        )} mg/day) can have mild blood-thinning effects. Ensure your doctor is aware of your supplement use.`,
      });
    }

    // Per-dose spike caution (captures bolus dosing days)
    if (perDoseEpaDhaMg > 1000) {
      safetyFlags.push({
        type: 'info',
        text: `Each supplement dose provides ~${round(
          perDoseEpaDhaMg
        )} mg EPA+DHA. Multiple doses in one day may exceed common caution thresholds.`,
      });
    }

    // Mercury Suggestions/Alerts
    if (mercuryConcern && food_high_mercury_servings_week > 0) {
      safetyFlags.push({
        type: 'warning',
        text: `High-mercury fish detected in diet (${food_high_mercury_servings_week.toFixed(
          1
        )} servings/wk). This poses a risk during pregnancy, lactation, or childhood.`,
      });
      suggestions.mercury.push(`AVOID high-mercury fish: ${HIGH_MERCURY_FISH_LIST.join(', ')}.`);
      suggestions.mercury.push(
        `CHOOSE low-mercury options (Aim for 2-3 servings/week total fish): ${LOW_MERCURY_FISH_LIST.join(', ')}.`
      );
      mercuryWarnings.forEach((warning) => suggestions.mercury.push(warning));
    } else if (mercuryConcern && totalFishServingsPerWeek > 3) {
      safetyFlags.push({
        type: 'info',
        text: `Total fish intake (${totalFishServingsPerWeek.toFixed(
          1
        )} servings/wk) exceeds the general recommendation of 2-3 servings (8-12 oz) per week during pregnancy/lactation.`,
      });
      suggestions.mercury.push(
        `Limit total fish intake to 2-3 servings (approx 8-12 oz) per week during pregnancy/lactation, focusing on low-mercury types: ${LOW_MERCURY_FISH_LIST.join(
          ', '
        )}.`
      );
    } else if (mercuryConcern) {
      suggestions.mercury.push(
        `Focus on low-mercury fish choices during pregnancy/lactation/childhood. Aim for 2-3 servings (8-12 oz) per week. Low-mercury options include: ${LOW_MERCURY_FISH_LIST.join(
          ', '
        )}.`
      );
    }

    // Food & Supplement Suggestions based on Gap
    if (epa_dha_gap > 0) {
      suggestions.food.push(`You are estimated to be ~${round(epa_dha_gap)} mg short of your daily EPA+DHA goal.`);

      const mgNeededWeekly = epa_dha_gap * 7;
      const mgPer3ozSalmon = (ozToGrams(3) / 100) * marineFoodData.salmon_atlantic.epa_dha_mg_per_100g;
      const mgPer3ozSardines = (ozToGrams(3) / 100) * marineFoodData.sardines_canned.epa_dha_mg_per_100g;
      const neededServingsSalmon = mgNeededWeekly / mgPer3ozSalmon;
      const neededServingsSardines = mgNeededWeekly / mgPer3ozSardines;

      suggestions.food.push(
        `To bridge this gap with food, consider adding ~${neededServingsSardines.toFixed(
          1
        )} servings (3 oz) of sardines OR ~${neededServingsSalmon.toFixed(1)} servings (3 oz) of salmon per week (ensure low mercury if needed).`
      );
      suggestions.food.push(`Other good sources include herring, mackerel (Atlantic), trout, anchovies.`);

      if (epa_dha_gap >= 250 || isVeganVegetarian) {
        suggestions.supplement.push(`If dietary changes are difficult or insufficient, consider a supplement providing EPA+DHA.`);
        if (isVeganVegetarian || (mercuryConcern && epa_dha_gap > 250)) {
          suggestions.supplement.push(
            `An Algal Oil supplement is a good vegan/vegetarian source and avoids mercury concerns. Look for one providing at least 250-300mg EPA+DHA per capsule (check label for specific EPA/DHA).`
          );
        } else if (!isVeganVegetarian) {
          suggestions.supplement.push(
            `A Fish Oil supplement (e.g., a 1000-1200mg capsule) typically provides 250-500mg combined EPA+DHA. Check the label for specific amounts.`
          );
        }
        const suppSuggestionDose = Math.min(1000, Math.ceil(epa_dha_gap / 100) * 100);
        if (suppSuggestionDose >= 250 && targetEpaDha < 2000) {
          suggestions.supplement.push(
            `A daily dose providing ~${suppSuggestionDose} mg combined EPA+DHA could help meet your target. Discuss dosage with your provider if gap is large or you have health conditions.`
          );
        } else if (targetEpaDha >= 2000) {
          suggestions.supplement.push(
            `Bridging a large gap, especially towards a therapeutic target for triglycerides, requires medical guidance and likely prescription supplements.`
          );
        }
      } else {
        suggestions.supplement.push(
          'Focus on increasing dietary sources (fatty fish, fortified foods) first to bridge the smaller EPA+DHA gap.'
        );
      }
    } else {
      suggestions.food.push(
        'Your estimated EPA+DHA intake appears to meet or exceed your target. Maintain a balanced intake of fatty fish or other sources.'
      );
      suggestions.supplement.push(
        'Ensure your total supplemental EPA+DHA intake stays within safe limits (generally <3000mg/day unless medically supervised).'
      );
    }

    if (ala_gap > 0) {
      suggestions.food.push(
        `Your estimated ALA intake is ~${round(
          ala_gap
        )} mg below the Adequate Intake. Increase sources like flaxseeds/oil, chia seeds, walnuts, canola/soybean oil.`
      );
    } else {
      suggestions.food.push('Your estimated ALA intake appears adequate.');
    }
    suggestions.lifestyle.push(
      'Remember that ALA conversion to EPA/DHA is limited for most people. Direct sources (fish, algae) are the most efficient way to increase EPA/DHA levels.'
    );

    // --- Set results (store NUMBERS, not strings) ---
    setCalculationResult({
      targetAla: targetAla,
      totalAla: totalAlaMgDay,
      alaGap: ala_gap,
      targetEpaDha: targetEpaDha,
      currentTargetEpaDhaRange: currentTargetEpaDhaRange,
      totalEpaDha: total_epa_dha,
      epaDhaGap: epa_dha_gap,
      foodEpaDha: food_epa_dha_total_mg_day,
      foodAla: food_ala_total_mg_day,
      convertedEpaFromAla: epa_from_ala_mg_day,
      convertedDhaFromAla: dha_from_ala_mg_day,
      suppEpa: supp_epa_mg_day,
      suppDha: supp_dha_mg_day,
      suppAla: supp_ala_mg_day,
      totalEpaApprox: total_epa,
      totalDhaApprox: total_dha,
      explanation: explanation.join('\n'),
      suggestions,
      safetyFlags,
    });
    setShowResults(true);
  };

  // --- JSX Structure ---
  return (
    <div className="omega3Calculator vitaminDCalculator">
      <h1>
        <span>
          <span role="img" aria-label="omega-3">🐟</span>{' '}
          Omega-3 Essential Fatty Acid Calculator
        </span>

        <span className="ai-promo-inline">
          <strong>Unlock Smarter Eating:</strong>{' '}
          Get a complete breakdown of vitamins, minerals, and fats plus a personalized
          weight-loss plan or training-focused nutrition with a single{' '}
          <a
            href="https://purehealthnexus.com/ai-nutrition-vitamins-minerals"
            className="ai-promo-link"
          >
            AI scan
          </a>.
        </span>
      </h1>
      <p className="important-note">
        <strong>Disclaimer:</strong> This calculator provides an ESTIMATE of your Omega-3 intake (ALA, EPA, DHA) based on
        general data and your inputs. It is NOT a substitute for professional medical advice, diagnosis, or treatment.
        Individual needs, absorption, and conversion rates vary. Consult your healthcare provider regarding your Omega-3
        status, especially if you are pregnant/lactating, have cardiovascular disease, high triglycerides, bleeding
        disorders, take anticoagulant medications, or are considering high-dose supplementation. Do not exceed 3 grams
        (3000 mg) of supplemental EPA+DHA per day without medical supervision due to potential risks. Pay attention to
        mercury content in fish, especially during pregnancy/childhood.
      </p>

      {/* --- Input Form --- */}
      <form onSubmit={calculateOmega3Status}>
        <h2>🧾 Your Information</h2>

        {/* Demographics */}
        <div className="form-section">
          <h3>👤 Demographics & Status</h3>
          <label>
            Age (Years):
            <input
              type="number"
              value={age}
              onChange={(e) => setAge(e.target.value)}
              min="0"
              max="120"
              placeholder="e.g., 35 (Optional)"
            />
            <small>Used for context, primary targets based on adult status below.</small>
          </label>
          <label>
            Sex Assigned at Birth:*
            <select value={sex} onChange={(e) => setSex(e.target.value)} required>
              <option value="">-- Select --</option>
              <option value="female">Female</option>
              <option value="male">Male</option>
            </select>
            <small>Affects ALA target and conversion rate.</small>
          </label>
          {sex === 'female' && (
            <>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={isPregnant}
                  onChange={(e) => setIsPregnant(e.target.checked)}
                  disabled={isLactating}
                />
                Currently Pregnant? (Increases ALA/DHA target & conversion)
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={isLactating}
                  onChange={(e) => setIsLactating(e.target.checked)}
                  disabled={isPregnant}
                />
                Currently Lactating? (Increases ALA/DHA target & conversion)
              </label>
            </>
          )}
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={isVeganVegetarian}
              onChange={(e) => setIsVeganVegetarian(e.target.checked)}
            />
            Vegetarian or Vegan? (Affects EPA/DHA sources & advice)
          </label>
        </div>

        {/* Fish & Seafood Diary */}
        <div className="form-section">
          <h3>🐟 Fish & Seafood Intake (Average WEEKLY)</h3>
          <small>List types of fish/seafood you eat per week. Use the Add button for multiple types.</small>
          {fishDiary.map((entry, index) => (
            <div key={index} className="fish-entry diet-grid">
              <label>
                Fish/Seafood Type #{index + 1}:
                <select
                  name="fishType"
                  value={entry.fishType}
                  onChange={(e) => handleFishDiaryChange(index, e)}
                  required={fishDiary.length > 0 && (entry.servingsPerWeek || entry.portionOz)}
                >
                  <option value="">-- Select Fish --</option>
                  {Object.keys(marineFoodData).map((key) => (
                    <option key={key} value={key}>
                      {key.replace(/_/g, ' ')} (~{marineFoodData[key].epa_dha_mg_per_100g} mg EPA+DHA/100g){' '}
                      {!marineFoodData[key].is_low_mercury ? '⚠️High Mercury' : ''}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Servings per WEEK:
                <input
                  type="number"
                  name="servingsPerWeek"
                  value={entry.servingsPerWeek}
                  onChange={(e) => handleFishDiaryChange(index, e)}
                  min="0"
                  step="0.1"
                  placeholder="e.g., 1, 0.5"
                  required={fishDiary.length > 0 && (entry.fishType || entry.portionOz)}
                />
              </label>
              <label>
                Portion Size (Ounces):
                <input
                  type="number"
                  name="portionOz"
                  value={entry.portionOz}
                  onChange={(e) => handleFishDiaryChange(index, e)}
                  min="1"
                  step="0.5"
                  placeholder="e.g., 3, 4"
                  required={fishDiary.length > 0 && (entry.fishType || entry.servingsPerWeek)}
                />
                <small>(Typical serving is 3-4 oz cooked)</small>
              </label>
              {fishDiary.length > 1 && (
                <button type="button" className="remove-btn" onClick={() => removeFishEntry(index)}>
                  Remove
                </button>
              )}
            </div>
          ))}
          <button type="button" onClick={addFishEntry}>
            + Add Another Fish Type
          </button>
          {(isPregnant || isLactating || mercuryConcern) && (
            <p className="warning-text small-text">
              Pregnancy/Lactation/Concerned: Choose low-mercury fish (salmon, sardines, trout, light tuna). Limit total
              to 8-12 oz/week. Avoid high-mercury fish (swordfish, shark, king mackerel, tilefish, albacore tuna).
            </p>
          )}
        </div>

        {/* Plant Omega-3s (ALA) */}
        <div className="form-section">
          <h3>🌱 Plant Omega-3 Sources (ALA - Average WEEKLY Servings)</h3>
          <small>Enter typical number of servings PER WEEK.</small>
          <div className="diet-grid">
            {Object.entries(plantAlaData).map(([key, data]) => (
              <label key={key}>
                {key.replace(/_/g, ' ')} ({data.serving_unit}):
                <input
                  type="number"
                  name={key}
                  value={plantSources[key] || ''}
                  onChange={handlePlantSourceChange}
                  min="0"
                  step="0.1"
                  placeholder="e.g., 2, 0.5"
                />
                <small>(~{data.ala_mg_per_serving} mg ALA)</small>
              </label>
            ))}
          </div>
        </div>

        {/* Fortified Foods */}
        <div className="form-section">
          <h3>🥛 Fortified Foods (Average WEEKLY Servings)</h3>
          <small>Enter servings PER WEEK. Provides modest EPA/DHA.</small>
          <div className="diet-grid">
            <label>
              Omega-3 Fortified Eggs:
              <input
                type="number"
                name="omega3_eggs"
                value={fortifiedFoods.omega3_eggs}
                onChange={handleFortifiedFoodChange}
                min="0"
                step="1"
                placeholder="e.g., 3"
              />
              <small>(~{FORTIFIED_EGG_EPA_DHA} mg EPA+DHA/egg)</small>
            </label>
            <label>
              Omega-3 Fortified Milk (cups):
              <input
                type="number"
                name="omega3_milk"
                value={fortifiedFoods.omega3_milk}
                onChange={handleFortifiedFoodChange}
                min="0"
                step="0.5"
                placeholder="e.g., 4"
              />
              <small>(~{FORTIFIED_MILK_EPA_DHA} mg EPA+DHA/cup)</small>
            </label>
            <label>
              Omega-3 Fortified Spreads (servings):
              <input
                type="number"
                name="omega3_spread"
                value={fortifiedFoods.omega3_spread}
                onChange={handleFortifiedFoodChange}
                min="0"
                step="1"
                placeholder="e.g., 5"
              />
              <small>(~{FORTIFIED_SPREAD_EPA_DHA} mg EPA+DHA/serving)</small>
            </label>
          </div>
        </div>

        {/* Supplements */}
        <div className="form-section">
          <h3>💊 Supplement Use</h3>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={takesSupplement}
              onChange={(e) => setTakesSupplement(e.target.checked)}
            />
            Do you currently take an Omega-3 supplement (Fish, Krill, Algal, Flax oil)?
          </label>
          {takesSupplement && (
            <div className="supplement-details">
              <label>
                Select Supplement Type (or 'Other'):
                <select value={selectedSupplementId} onChange={handleSupplementSelection} required={takesSupplement}>
                  <option value="">-- Select --</option>
                  {supplementCatalog.map((supp) => (
                    <option key={supp.id} value={supp.id}>
                      {supp.name} {supp.epa_mg > 0 ? `(EPA ${supp.epa_mg})` : ''}{' '}
                      {supp.dha_mg > 0 ? `(DHA ${supp.dha_mg})` : ''} {supp.ala_mg > 0 ? `(ALA ${supp.ala_mg})` : ''}
                    </option>
                  ))}
                </select>
              </label>
              {selectedSupplementId === 'other' && (
                <>
                  <label>
                    Custom EPA per Dose (mg):
                    <input
                      type="number"
                      value={customSupplementEPA}
                      onChange={(e) => setCustomSupplementEPA(e.target.value)}
                      min="0"
                      placeholder="e.g., 300"
                    />
                  </label>
                  <label>
                    Custom DHA per Dose (mg):
                    <input
                      type="number"
                      value={customSupplementDHA}
                      onChange={(e) => setCustomSupplementDHA(e.target.value)}
                      min="0"
                      placeholder="e.g., 200"
                    />
                  </label>
                  <label>
                    Custom ALA per Dose (mg):
                    <input
                      type="number"
                      value={customSupplementALA}
                      onChange={(e) => setCustomSupplementALA(e.target.value)}
                      min="0"
                      placeholder="e.g., 0"
                    />
                  </label>
                </>
              )}
              <label>
                Number of Doses Per WEEK:
                <input
                  type="number"
                  value={supplementDosesPerWeek}
                  onChange={(e) => setSupplementDosesPerWeek(e.target.value)}
                  required={takesSupplement}
                  min="1"
                  placeholder="e.g., 7 (for daily), 14"
                />
              </label>
              <small>Enter total doses taken across the entire week.</small>
            </div>
          )}
        </div>

        {/* n-6 Proxy & Health Flags */}
        <div className="form-section">
          <h3>🚩 Health Profile & Diet Factors</h3>
          <fieldset>
            <legend>Check any that apply:</legend>
            <label className="checkbox-label indented">
              <input
                type="checkbox"
                checked={highN6Intake}
                onChange={(e) => setHighN6Intake(e.target.checked)}
              />
              Diet likely high in Omega-6? (e.g., frequent processed foods, use of corn/soybean/sunflower oil)
              <small> (Reduces ALA conversion)</small>
            </label>
            <label className="checkbox-label indented">
              <input type="checkbox" name="hasCVD" checked={healthFlags.hasCVD} onChange={handleHealthFlagChange} />
              History of Cardiovascular Disease (Heart attack, stroke, etc.)?
              <small> (Increases EPA+DHA target)</small>
            </label>
            <label className="checkbox-label indented">
              <input
                type="checkbox"
                name="hasHyperTG"
                checked={healthFlags.hasHyperTG}
                onChange={handleHealthFlagChange}
              />
              Diagnosed High Triglycerides?
              <strong className="warning-text"> (Requires medical supervision for therapeutic doses)</strong>
              <small> (Significantly increases EPA+DHA target)</small>
            </label>
            <label className="checkbox-label indented">
              <input
                type="checkbox"
                name="hasBleedingDisorder"
                checked={healthFlags.hasBleedingDisorder}
                onChange={handleHealthFlagChange}
              />
              Bleeding disorder or bruise easily?
              <small> (Increases caution with high-dose supplements)</small>
            </label>
            <label className="checkbox-label indented">
              <input
                type="checkbox"
                name="takesAnticoagulant"
                checked={healthFlags.takesAnticoagulant}
                onChange={handleHealthFlagChange}
              />
              Take blood-thinning medication (Warfarin, Coumadin, Eliquis, Xarelto, Pradaxa, high-dose Aspirin)?
              <strong className="warning-text"> (Increases caution with supplements)</strong>
            </label>
          </fieldset>
        </div>

        <button type="submit">Calculate Omega-3 Status</button>
      </form>

      {/* --- Results Display --- */}
      {showResults && calculationResult && (
        <div className="results-section">
          <h2>📊 Estimated Omega-3 Status</h2>

          {/* Safety Flags Display */}
          {calculationResult.safetyFlags.length > 0 && (
            <div className="safety-alerts">
              <h4>⚠️ Safety Alerts & Important Considerations</h4>
              <ul>
                {calculationResult.safetyFlags.map((flag, index) => (
                  <li key={index} className={`alert-${flag.type}`}>
                    <strong>{flag.type.toUpperCase()}:</strong> {flag.text}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Dual Gauge Implementation */}
          <div className="result-gauge-placeholder dual-gauge">
            <p>
              <strong>Intake vs. Targets</strong>
            </p>
            <div className="gauge-pair">
              {/* ALA Gauge */}
              <div className="gauge-container">
                <label className="gauge-title">ALA (Plant Omega-3)</label>
                <div
                  className="gauge-bar omega3-gauge-ala"
                  style={{
                    '--value': calculationResult.totalAla,
                    '--target': calculationResult.targetAla,
                    '--fill-percent': `${Math.min(
                      100,
                      (calculationResult.totalAla / calculationResult.targetAla) * 100
                    )}%`,
                  }}
                >
                  <div className="gauge-fill"></div>
                  <span className="gauge-marker" style={{ left: `var(--fill-percent)` }}>
                    {formatMg(calculationResult.totalAla)} mg
                  </span>
                </div>
                <span className="gauge-target-label">AI Target: {formatMg(calculationResult.targetAla)} mg</span>
              </div>
              {/* EPA+DHA Gauge */}
              <div className="gauge-container">
                <label className="gauge-title">EPA + DHA (Marine/Algal Omega-3)</label>
                <div
                  className="gauge-bar omega3-gauge-epadha"
                  style={{
                    '--value': calculationResult.totalEpaDha,
                    '--target': calculationResult.targetEpaDha,
                    '--fill-percent': `${Math.min(
                      100,
                      (calculationResult.totalEpaDha / calculationResult.targetEpaDha) * 100
                    )}%`,
                  }}
                >
                  <div className="gauge-fill"></div>
                  <span className="gauge-marker" style={{ left: `var(--fill-percent)` }}>
                    {formatMg(calculationResult.totalEpaDha)} mg
                  </span>
                </div>
                <span className="gauge-target-label">Rec. Target: {formatMg(calculationResult.targetEpaDha)} mg</span>
              </div>
            </div>
            <span className="gauge-label">
              ALA: {formatMg(calculationResult.totalAla)} / {formatMg(calculationResult.targetAla)} mg | EPA+DHA:{' '}
              {formatMg(calculationResult.totalEpaDha)} / {formatMg(calculationResult.targetEpaDha)} mg
            </span>
          </div>

          <div className="results-grid">
            <div className="result-item">
              <span className="result-label">ALA Target (AI):</span>
              <span className="result-value">{formatMg(calculationResult.targetAla)} mg/day</span>
            </div>
            <div className="result-item">
              <span className="result-label">Est. Total ALA Intake:</span>
              <span
                className={`result-value status-${calculationResult.alaGap > calculationResult.targetAla * 0.3
                  ? 'low'
                  : calculationResult.alaGap > 0
                    ? 'borderline'
                    : 'adequate'
                  }`}
              >
                {formatMg(calculationResult.totalAla)} mg/day
              </span>
              <small className="result-note">
                (Food: {formatMg(calculationResult.foodAla)}mg, Supp: {formatMg(calculationResult.suppAla)}mg)
              </small>
            </div>

            <div className="result-item">
              <span className="result-label">EPA+DHA Target (Rec.):</span>
              <span className="result-value">{formatMg(calculationResult.targetEpaDha)} mg/day</span>
              <small className="result-note">
                (Range: {formatMg(calculationResult.currentTargetEpaDhaRange.min)}-
                {formatMg(calculationResult.currentTargetEpaDhaRange.max)}mg
                {calculationResult.currentTargetEpaDhaRange.dha_emphasis
                  ? `, DHA ≥ ${formatMg(calculationResult.currentTargetEpaDhaRange.dha_emphasis)}mg`
                  : ''}
                )
              </small>
            </div>

            <div className="result-item">
              <span className="result-label">Est. Total EPA+DHA Intake:</span>
              <span
                className={`result-value status-${calculationResult.epaDhaGap > calculationResult.targetEpaDha * 0.3
                  ? 'low'
                  : calculationResult.epaDhaGap > 0
                    ? 'borderline'
                    : 'adequate'
                  }`}
              >
                {formatMg(calculationResult.totalEpaDha)} mg/day
              </span>
              <small className="result-note">
                (Food: {formatMg(calculationResult.foodEpaDha)}mg, ALA Conv:{' '}
                {formatMg(calculationResult.convertedEpaFromAla + calculationResult.convertedDhaFromAla)}mg, Supp:{' '}
                {formatMg(calculationResult.suppEpa + calculationResult.suppDha)}mg)
              </small>
            </div>

            <div className="result-item">
              <span className="result-label">EPA+DHA vs Target:</span>
              <span
                className={`result-value status-${calculationResult.epaDhaGap > calculationResult.targetEpaDha * 0.3
                  ? 'low'
                  : calculationResult.epaDhaGap > 0
                    ? 'borderline'
                    : 'adequate'
                  }`}
              >
                {calculationResult.epaDhaGap > 0
                  ? `Deficit (~${formatMg(calculationResult.epaDhaGap)} mg)`
                  : `Adequate / Surplus`}
              </span>
            </div>

            <div className="result-item">
              <span className="result-label">Approx. Total EPA:</span>
              <span className="result-value">{formatMg(calculationResult.totalEpaApprox)} mg/day</span>
            </div>
            <div className="result-item">
              <span className="result-label">Approx. Total DHA:</span>
              <span className="result-value">{formatMg(calculationResult.totalDhaApprox)} mg/day</span>
              {calculationResult.currentTargetEpaDhaRange.dha_emphasis &&
                calculationResult.totalDhaApprox < calculationResult.currentTargetEpaDhaRange.dha_emphasis && (
                  <small className="warning-text result-note">
                    {' '}
                    (Below recommended DHA level of {formatMg(calculationResult.currentTargetEpaDhaRange.dha_emphasis)}mg
                    for pregnancy/lactation)
                  </small>
                )}
            </div>

            <div className="result-item">
              <span className="result-label">Supplemental EPA+DHA:</span>
              <span className="result-value">
                {formatMg(calculationResult.suppEpa + calculationResult.suppDha)} mg/day
              </span>
              <small className="result-note">(Caution &gt;1000mg w/ anticoagulants, &gt;3000mg general max advisory)</small>
            </div>
          </div>

          <h3>💡 Guidance & Suggestions</h3>
          <p className="gap-statement">
            {calculationResult.epaDhaGap > 0
              ? `You appear to be about ${formatMg(calculationResult.epaDhaGap)} mg short of your personalized daily EPA+DHA goal.`
              : `Your estimated EPA+DHA intake meets or exceeds your personalized daily goal.`}{' '}
            {calculationResult.alaGap > 0
              ? `Your ALA intake is about ${formatMg(calculationResult.alaGap)} mg below the Adequate Intake.`
              : `Your ALA intake appears adequate.`}
          </p>

          {calculationResult.suggestions.mercury.length > 0 && (
            <div className="suggestions-box safety-advice">
              <h4>🎣 Mercury & Fish Choice Advice:</h4>
              <ul>{calculationResult.suggestions.mercury.map((s, i) => <li key={`merc-${i}`}>{s}</li>)}</ul>
            </div>
          )}
          {calculationResult.suggestions.food.length > 0 && (
            <div className="suggestions-box">
              <h4>🍽️ Food Suggestions:</h4>
              <ul>{calculationResult.suggestions.food.map((s, i) => <li key={`food-${i}`}>{s}</li>)}</ul>
            </div>
          )}
          {calculationResult.suggestions.supplement.length > 0 && (
            <div className="suggestions-box">
              <h4>💊 Supplement Considerations:</h4>
              <ul>{calculationResult.suggestions.supplement.map((s, i) => <li key={`supp-${i}`}>{s}</li>)}</ul>
            </div>
          )}
          {calculationResult.suggestions.lifestyle.length > 0 && (
            <div className="suggestions-box">
              <h4>🌱 Lifestyle & Diet Tips:</h4>
              <ul>{calculationResult.suggestions.lifestyle.map((s, i) => <li key={`life-${i}`}>{s}</li>)}</ul>
            </div>
          )}
          {calculationResult.suggestions.safety.length > 0 && (
            <div className="suggestions-box safety-advice">
              <h4>🛡️ Specific Safety Advice:</h4>
              <ul>{calculationResult.suggestions.safety.map((s, i) => <li key={`safe-${i}`}>{s}</li>)}</ul>
            </div>
          )}

          <h3>📝 Calculation Explanation</h3>
          <pre className="explanation-box">{calculationResult.explanation}</pre>

          <p className="important-note">
            <strong>Reminder:</strong> This is an educational tool, not medical advice. Individual conversion rates and
            needs vary. Discuss these estimates and any planned changes (diet, supplements) with your healthcare
            provider, especially considering your specific health conditions, medications, and life stage
            (pregnancy/lactation).
          </p>
          <a href="#omega3-education" className="scroll-link">
            Learn more about Omega-3s below
          </a>
        </div>
      )}

      {/* --- Educational Content (Omega-3 specific) --- */}
      <div id="omega3-education" className="educational-section">
        <h2>Understanding Omega-3 Fatty Acids</h2>

        {/* Content Block 1: Basics */}
        <div className="content-block">
          <h3>1. Omega-3 Basics (Biology, Forms & Safety)</h3>
          <dl>
            <dt>Physiological Roles:</dt>
            <dd>
              Omega-3s are essential polyunsaturated fatty acids. They are key structural components of cell membranes,
              particularly abundant in the brain and retina (especially DHA). They play vital roles in regulating
              inflammation through the production of eicosanoids and specialized pro-resolving mediators (resolvins,
              protectins). Omega-3s also contribute to cardiovascular health by improving triglyceride levels and
              endothelial function, and are crucial for fetal and infant neurodevelopment. (Source: ODS)
            </dd>

            <dt>Main Forms:</dt>
            <dd>
              <ul>
                <li>
                  <strong>ALA (Alpha-Linolenic Acid, 18:3 n-3):</strong> An essential fatty acid the body cannot make.
                  Found in plant sources like flaxseeds, chia seeds, walnuts, and certain vegetable oils (canola,
                  soybean).
                </li>
                <li>
                  <strong>EPA (Eicosapentaenoic Acid, 20:5 n-3):</strong> A long-chain omega-3, primarily found in
                  marine sources (fatty fish, krill) and algae. Can be synthesized from ALA, but inefficiently.
                </li>
                <li>
                  <strong>DHA (Docosahexaenoic Acid, 22:6 n-3):</strong> Another long-chain omega-3, crucial for brain
                  and eye structure. Primarily found in marine sources and algae. Can be synthesized from ALA (even less
                  efficiently than EPA) or elongated from EPA.
                </li>
              </ul>
              EPA and DHA are considered the most biologically active forms regarding many health benefits
              (cardiovascular, anti-inflammatory). (Source: ODS)
            </dd>

            <dt>Conversion Limits:</dt>
            <dd>
              The body can convert ALA into EPA and subsequently into DHA, but this process is limited and influenced by
              several factors. In healthy adults, typical conversion rates are estimated:
              <ul>
                <li>ALA to EPA: ≈ 5–10%</li>
                <li>ALA to DHA: ≈ 2–5%</li>
              </ul>
              Conversion rates may be slightly higher in women (especially during pregnancy) possibly due to hormonal
              influences. High intake of Omega-6 fatty acids (linoleic acid, common in many vegetable oils and processed
              foods) competes for the same enzymes, significantly reducing ALA conversion to EPA and DHA. (Source:
              PubMed, PMC)
            </dd>

            <dt>Safety / UL (Upper Limit):</dt>
            <dd>
              There is no formal Tolerable Upper Intake Level (UL) established by the Institute of Medicine due to
              insufficient data on adverse effects from high food intake. However, concerns exist regarding high-dose
              SUPPLEMENTS:
              <ul>
                <li>
                  The FDA advises consumers not to exceed 3 g/day (3000 mg/day) of combined EPA + DHA from supplements
                  without healthcare provider consultation, due to potential increased risk of bleeding (especially at
                  doses &gt;3g/day) and possible effects on immune function. (Source: FDA)
                </li>
                <li>
                  The FDA has authorized a qualified health claim linking EPA+DHA consumption to reduced risk of
                  hypertension, stating that consuming EPA and DHA combined may reduce the risk of hypertension, but the
                  evidence is inconsistent and inconclusive. They note that doses up to 5 g/day seem safe. (Source:
                  FDA)
                </li>
                <li>
                  Individuals on blood-thinning medications (anticoagulants like warfarin, DOACs) or with bleeding
                  disorders should exercise particular caution, as omega-3 supplements can have anti-platelet effects.
                  Consultation with a doctor is essential before taking supplements, especially at doses above 1 g/day
                  (1000 mg/day).
                </li>
              </ul>
            </dd>
          </dl>
        </div>

        {/* Content Block 2: Intakes & Guidelines */}
        <div className="content-block">
          <h3>2. Reference Intakes & Expert Guidelines</h3>
          <table>
            <thead>
              <tr>
                <th>Metric</th>
                <th>Value</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>AI for ALA (IOM)</td>
                <td>1.6 g/day (Men), 1.1 g/day (Women)</td>
                <td>
                  Based on median US intake. Higher in pregnancy (1.4 g) & lactation (1.3 g). Easily met via plant oils,
                  nuts, seeds.
                </td>
              </tr>
              <tr>
                <td>EPA + DHA Target (General Health)</td>
                <td>250–500 mg/day</td>
                <td>
                  Consensus from WHO, EFSA, AHA. Achievable with ≈1–2 servings of fatty fish per week. This calculator
                  uses 300mg as a base target.
                </td>
              </tr>
              <tr>
                <td>EPA + DHA (Pregnancy/Lactation)</td>
                <td>Target ≥300 mg/day, including ≥200 mg/day DHA</td>
                <td>
                  Crucial for fetal/infant brain and eye development. Some groups suggest higher DHA (≥300mg).
                  Low-mercury sources essential. (Source: PMC)
                </td>
              </tr>
              <tr>
                <td>EPA + DHA (Cardiac Benefit - AHA)</td>
                <td>Eat ≥2 servings (≈3 oz cooked) fatty fish/week</td>
                <td>
                  Provides average of ~500 mg EPA+DHA/day. For individuals with known heart disease, AHA suggests aiming
                  for ~1 g/day (1000 mg/day) EPA+DHA, often via supplements (consult doctor). (Source: heart.org)
                </td>
              </tr>
              <tr>
                <td>EPA + DHA (Therapeutic for Hypertriglyceridemia)</td>
                <td>2–4 g/day (2000–4000 mg/day)</td>
                <td>Prescription-strength fish oil (high EPA ± DHA) used under medical supervision to lower high TGs.</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Content Block 3: Groups to Flag */}
        <div className="content-block">
          <h3>3. Groups with Special Considerations</h3>
          <ul>
            <li>
              <strong>Low-Fish Eaters / Vegetarians / Vegans:</strong> May have very low direct EPA+DHA intake. Rely on
              inefficient ALA conversion or need algal oil supplements for direct EPA/DHA.
            </li>
            <li>
              <strong>Pregnant & Lactating Women:</strong> Increased DHA needs for fetal/infant development. Must choose
              low-mercury fish sources or use algal supplements.
            </li>
            <li>
              <strong>Children & Teens:</strong> Important for ongoing brain development; dietary intake is often below
              optimal levels.
            </li>
            <li>
              <strong>Cardiovascular Disease Patients:</strong> Evidence supports higher intake (around 1000 mg/day
              EPA+DHA) for secondary prevention (consult doctor).
            </li>
            <li>
              <strong>Individuals with High Triglycerides:</strong> Very high doses (2-4 g/day) under medical
              supervision are effective for lowering TGs.
            </li>
            <li>
              <strong>People with Inflammatory Conditions (e.g., Rheumatoid Arthritis) or Mental Health Conditions (e.g.,
                Depression):</strong> Omega-3s (particularly EPA) have anti-inflammatory and potential mood benefits.
              Research is ongoing regarding optimal doses.
            </li>
            <li>
              <strong>People on Anticoagulants / with Bleeding Disorders:</strong> Increased risk of bleeding with
              supplemental EPA+DHA, especially &gt;1 g/day. Requires medical consultation.
            </li>
          </ul>
        </div>

        {/* Sources Section */}
        <div className="content-block">
          <h3>Sources</h3>
          <p className="sources">
            National Institutes of Health, Office of Dietary Supplements (ODS). (n.d.). <em>Omega-3 Fatty Acids Fact
              Sheet for Health Professionals</em>. Retrieved [April-14-2025], from
            https://ods.od.nih.gov/factsheets/Omega3FattyAcids-HealthProfessional/ <br />
            National Institutes of Health, Office of Dietary Supplements (ODS). (n.d.). <em>Omega-3 Fatty Acids Fact
              Sheet for Consumers</em>. Retrieved [April-14-2025], from
            https://ods.od.nih.gov/factsheets/Omega3FattyAcids-Consumer/ <br />
            U.S. Food and Drug Administration (FDA). (2019, June 19). <em>FDA Announces New Qualified Health Claims for
              EPA and DHA Omega-3 Consumption and the Risk of Hypertension and Coronary Heart Disease</em>. Retrieved
            [April-14-2025], from
            https://www.fda.gov/food/cfsan-constituent-updates/fda-announces-new-qualified-health-claims-epa-and-dha-omega-3-consumption-and-risk-hypertension-and
            <br />
            U.S. Food and Drug Administration (FDA). (n.d.). <em>Advice about Eating Fish</em>. Retrieved [April-14-2025],
            from https://www.fda.gov/food/consumers/advice-about-eating-fish <br />
            American Heart Association (AHA). (n.d.). <em>Fish and Omega-3 Fatty Acids</em>. Retrieved [April-14-2025],
            from
            https://www.heart.org/en/healthy-living/healthy-eating/eat-smart/fats/fish-and-omega-3-fatty-acids <br />
            Harris, W. S., Mozaffarian, D., Rimm, E., Kris-Etherton, P., Rudel, L. L., Appel, L. J., ... &amp; Sacks, F.
            (2009). Omega-6 fatty acids and risk for cardiovascular disease: a science advisory from the American Heart
            Association. <em>Circulation</em>, <em>119</em>(6), 902–907. <br />
            Brenna, J. T., Salem Jr, N., Sinclair, A. J., &amp; Cunnane, S. C. (2009). α-Linolenic acid supplementation
            and conversion to n-3 long-chain polyunsaturated fatty acids in humans. <em>PLEFA</em>, <em>80</em>(2-3),
            85-91. <br />
            Coletta, J. M., Bell, S. J., &amp; Roman, A. S. (2010). Omega-3 Fatty acids and pregnancy. <em>Reviews in
              Obstetrics and Gynecology</em>, <em>3</em>(4), 163–171. <br />
            USDA FoodData Central. (n.d.). Retrieved [April-14-2025], from https://fdc.nal.usda.gov/
          </p>
        </div>
      </div>
    </div>
  );
}

export default Omega3EssentialFattyAcidCalculator;
